import type {
  AdminSubscriptionDetail,
  AdminSubscriptionsListResponse,
  PatchSubscriptionStatusPayload,
  AdminSubscription,
  UpdateSubscriptionPayload,
  RenewSubscriptionPayload,
} from '../types/subscription.types';
import { apiFetch } from './client';
import { parseJsonResponse, type PaginationMeta } from './parseResponse';

function buildList(
  data: AdminSubscription[] | undefined,
  meta?: PaginationMeta,
  perPage = 15,
): AdminSubscriptionsListResponse {
  return {
    data: Array.isArray(data) ? data : [],
    current_page: meta?.current_page ?? 1,
    per_page: meta?.per_page ?? perPage,
    total: meta?.total ?? (Array.isArray(data) ? data.length : 0),
    last_page: meta?.last_page ?? 1,
    first_page_url: null,
    from: null,
    last_page_url: null,
    next_page_url: null,
    path: '',
    prev_page_url: null,
    to: null,
  };
}

function unwrapSubscriptionDetail(body: unknown): AdminSubscriptionDetail {
  if (body && typeof body === 'object' && body !== null) {
    const o = body as Record<string, unknown>;
    const inner = o.data;
    if (
      inner &&
      typeof inner === 'object' &&
      !Array.isArray(inner) &&
      ('id' in inner || 'tenant_id' in inner)
    ) {
      return inner as AdminSubscriptionDetail;
    }
  }
  return body as AdminSubscriptionDetail;
}

export async function fetchSubscriptionsList(
  page: number,
  perPage = 15,
  opts?: { status?: string; search?: string },
): Promise<
  | { ok: true; list: AdminSubscriptionsListResponse }
  | { ok: false; message: string; unauthorized?: boolean }
> {
  const qs = new URLSearchParams({
    per_page: String(perPage),
    page: String(page),
  });
  if (opts?.status) qs.set('status', opts.status);
  if (opts?.search) qs.set('search', opts.search);
  const res = await apiFetch(`/platform/subscriptions?${qs.toString()}`);
  const out = await parseJsonResponse<AdminSubscription[]>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return { ok: true, list: buildList(out.data, out.meta, perPage) };
}

export async function fetchSubscription(
  subscriptionId: number,
): Promise<
  | { ok: true; subscription: AdminSubscriptionDetail }
  | { ok: false; message: string; unauthorized?: boolean }
> {
  const res = await apiFetch(`/platform/subscriptions/${encodeURIComponent(String(subscriptionId))}`);
  const out = await parseJsonResponse<Record<string, unknown>>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return { ok: true, subscription: unwrapSubscriptionDetail(out.data) };
}

export async function patchSubscriptionStatus(
  subscriptionId: number,
  payload: PatchSubscriptionStatusPayload,
): Promise<{ ok: true } | { ok: false; message: string; unauthorized?: boolean }> {
  return updateSubscription(subscriptionId, { status: payload.status });
}

export async function updateSubscription(
  subscriptionId: number,
  payload: UpdateSubscriptionPayload,
): Promise<
  | { ok: true; data: AdminSubscription }
  | { ok: false; message: string; unauthorized?: boolean }
> {
  const res = await apiFetch(`/platform/subscriptions/${encodeURIComponent(String(subscriptionId))}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const out = await parseJsonResponse<AdminSubscription>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return { ok: true, data: out.data };
}

export async function renewSubscription(
  subscriptionId: number,
  payload: RenewSubscriptionPayload,
): Promise<
  | { ok: true; data: AdminSubscription }
  | { ok: false; message: string; unauthorized?: boolean }
> {
  const res = await apiFetch(
    `/platform/subscriptions/${encodeURIComponent(String(subscriptionId))}/renew`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    },
  );
  const out = await parseJsonResponse<AdminSubscription>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return { ok: true, data: out.data };
}

export async function deleteSubscription(
  subscriptionId: number,
): Promise<{ ok: true } | { ok: false; message: string; unauthorized?: boolean }> {
  const res = await apiFetch(`/platform/subscriptions/${encodeURIComponent(String(subscriptionId))}`, {
    method: 'DELETE',
  });
  const out = await parseJsonResponse<Record<string, unknown>>(res);
  if (out.ok === false) {
    return { ok: false, message: out.message, unauthorized: out.unauthorized };
  }
  return { ok: true };
}
